import { PathsV1ModelFamilySearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useModelFamilySearchQuery } from "@/common/api/model-family/use-model-family-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getModelFamilyMergeLabel } from "../_helpers/model-merge-labels";

const PAGE_SIZE = 50;

interface ModelFamilyComboboxProps {
  value: string;
  onChange: (uuid: string, label: string) => void;
  excludeUuid?: string;
  disabled?: boolean;
}

export const ModelFamilyCombobox = ({ value, onChange, excludeUuid, disabled }: ModelFamilyComboboxProps) => {
  const { t } = useTranslation("common");
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: PAGE_SIZE,
  });
  const { data, isPending } = useModelFamilySearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
        extend: [PathsV1ModelFamilySearchPostParametersQueryExtend.Brand],
      },
      body: {},
    },
  });
  const items = useMemo(() => data?.data ?? [], [data]);
  const options = useMemo<ComboboxOption[]>(
    () =>
      items
        .filter((modelFamily) => modelFamily.uuid !== excludeUuid)
        .map((modelFamily) => ({ value: modelFamily.uuid, label: getModelFamilyMergeLabel(modelFamily) })),
    [excludeUuid, items],
  );
  const { isLoading, hasMore, remainingCount, onLoadMore, isLoadingMore, onSearchChange } = createPaginationState(
    data?.meta?.total ?? 0,
    items.length,
    isPending,
  );
  const placeholder = t("SearchModelFamilies");

  return (
    <Combobox
      options={options}
      value={value}
      onChange={(next) => {
        const selected = items.find((modelFamily) => modelFamily.uuid === next);
        onChange(next as string, selected ? getModelFamilyMergeLabel(selected) : "");
      }}
      onSearchChange={onSearchChange}
      shouldFilter={false}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      remainingCount={remainingCount}
      onLoadMore={onLoadMore}
      placeholder={placeholder}
      searchPlaceholder={placeholder}
      disabled={disabled}
    />
  );
};
